import { useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { getCameraPermissionStatus, generateRandomId } from '../helpers/WebcamHelper'
import cameraStreamManager from '../helpers/cameraStreamManager'

const useCameraPreview = () => {
  const videoRef = useRef(null)
  const subscriberIdRef = useRef(null)
  if (subscriberIdRef.current === null) {
    subscriberIdRef.current = `preview_${generateRandomId()}`
  }
  const cancelledRef = useRef(false)

  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const [isDenied, setIsDenied] = useState(false)

  const attachStream = (stream) => {
    if (videoRef.current) videoRef.current.srcObject = stream
    setIsReady(true)
    setError(null)
    setIsDenied(false)
  }

  // NotAllowedError covers both `denied` and a dismissed `prompt`. Query the
  // Permissions API to disambiguate so the UI can route the user correctly.
  const refreshPermissionFlags = async () => {
    try {
      const status = await getCameraPermissionStatus()
      if (cancelledRef.current) return
      setIsDenied(status === 'denied')
    } catch (err) {
      if (cancelledRef.current) return
      console.warn('[useCameraPreview] permissions query unsupported', err)
      setIsDenied(true)
    }
  }

  const handleAcquireError = async (err) => {
    console.warn('[useCameraPreview] could not acquire stream', err)
    if (err?.name === 'NotAllowedError') {
      await refreshPermissionFlags()
    } else {
      if (cancelledRef.current) return
      setIsDenied(false)
    }
    if (cancelledRef.current) return
    setError(err?.message || 'unavailable')
    setIsReady(false)
  }

  const retry = async () => {
    setIsRetrying(true)
    try {
      try {
        const status = await getCameraPermissionStatus()
        if (status === 'denied') {
          setIsDenied(true)
          setError('NotAllowedError')
          return
        }
        setIsDenied(false)
      } catch (err) {
        console.warn('[useCameraPreview] permissions query unsupported; falling through to getUserMedia', err)
      }

      try {
        const stream = await cameraStreamManager.refreshIfDead()
        if (cancelledRef.current) return
        if (stream) attachStream(stream)
      } catch (err) {
        console.warn('[useCameraPreview] failed to refresh camera stream', err)
        if (cancelledRef.current) return
        await handleAcquireError(err)
      }
    } finally {
      setIsRetrying(false)
    }
  }

  useEffect(() => {
    if (isMobile) return
    cancelledRef.current = false
    const id = subscriberIdRef.current

    const onStreamChange = (stream, info) => {
      if (cancelledRef.current) return
      if (info.reason === 'ended') {
        if (videoRef.current) videoRef.current.srcObject = null
        setIsReady(false)
        setError('device_ended')
        return
      }
      if (info.reason === 'acquired' && stream) {
        attachStream(stream)
      }
    }

    cameraStreamManager.subscribe(id, onStreamChange)
      .then(stream => {
        if (cancelledRef.current) return
        attachStream(stream)
      })
      .catch(err => {
        if (cancelledRef.current) return
        handleAcquireError(err)
      })

    return () => {
      cancelledRef.current = true
      cameraStreamManager.release(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { videoRef, isReady, error, retry, isRetrying, isDenied }
}

export default useCameraPreview
